import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getAccessToken } from "../../../utils/authStorage";
import styles from "./admin.module.css";
import VoucherManagement from "./VoucherManagement";
import UserManagement from "./UserManagement";
import BannerManager from "./BannerManager";
import { GameManagement } from "./GameManagement";
import BlogManagement from "./BlogManagement";
import ProfitCalculator from "./ProfitCalculator";
import OrderManagement from "./OrderManagement";
import NotificationManager from "./NotificationManager";
import { PricingManagement } from "./PricingManagement";
import ProviderCurrencyManagement from "./ProviderCurrencyManagement"; //
const AdminPanel = () => {
  const { user } = useAuth();
  // Normalize API_BASE to ensure it doesn't have a trailing slash, preventing double slashes in fetch calls.
  const API_BASE = (import.meta.env.VITE_API_URL || "")
    .trim()
    .replace(/\/$/, "");
  const token = getAccessToken();
  const [activeTab, setActiveTab] = useState("games");
  const [balances, setBalances] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // B) On first load (and whenever user changes), fetch list of games and balances
  useEffect(() => {
    if (user?.role === "admin") {
      fetchBalances();
    }
  }, [user]);

  // 1) Fetch balances endpoint (aggregate of 3 providers)
  const fetchBalances = async () => {
    try {
      const res = await fetch(`${API_BASE}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || !ct.includes("application/json")) {
        console.error("fetchBalances failed:", await res.text());
        return;
      }
      const data = await res.json();
      if (data.success) {
        setBalances(data.balances);
      }
    } catch (err) {
      console.error("Error fetching balances:", err);
    }
  };

  const formatBalance = (val) => {
    if (val && typeof val === "object" && val.saldo !== undefined) {
      return val.saldo;
    }
    return val || "0.00";
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  if (user?.role !== "admin") {
    return (
      <div
        className={`${styles.accessDenied} d-flex align-items-center justify-content-center`}
      >
        <div className="text-center">
          <h2 className="display-6 mb-3">Access Denied</h2>
          <p className="text-muted">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminContainer}>
      <div className="container-fluid py-4">
        <div className="mb-4">
          <h1 className="display-5 fw-bold">Admin Panel</h1>
        </div>

        {/* Balance Display */}
        {balances && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">API Balances</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Smile.one</h6>
                        <h4 className="text-primary">
                          ${formatBalance(balances.smileone)}
                        </h4>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Yokcash</h6>
                        <h4 className="text-success">
                          ${formatBalance(balances.yokcash)}
                        </h4>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Hopestore</h6>
                        <h4 className="text-warning">
                          ${formatBalance(balances.hopestore)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert Messages */}
        {error && (
          <div className="alert alert-danger alert-dismissible" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
            ></button>
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-dismissible" role="alert">
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
            ></button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-4">
          <ul className={styles.navTabs}>
            {[
              "games",
              "users",
              "orders",
              "voucher",
              "analytics",
              "pricing",
              "banner",
              "blog",
              "notifications",
              "provider-currencies",
            ].map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  data-tab={tab}
                  className={`${styles.navLink} text-capitalize ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "pricing" ? "💰 Pricing" : tab.replace("-", " ")}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Games Tab */}
        {activeTab === "games" && <GameManagement />}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="row">
            <div className="col-12">
              <UserManagement />
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div className="row">
            <div className="col-12">
              <OrderManagement />
            </div>
          </div>
        )}

        {/* Voucher Tab */}
        {activeTab === "voucher" && (
          <div className="row">
            <div className="col-12">
              <VoucherManagement />
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="row">
            <div className="col-12">
              <ProfitCalculator />
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div className="row">
            <div className="col-12">
              <PricingManagement />
            </div>
          </div>
        )}

        {/* Banner Tab */}
        {activeTab === "banner" && (
          <div className="row">
            <div className="col-12">
              <BannerManager />
            </div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === "blog" && (
          <div className="row">
            <div className="col-12">
              <BlogManagement />
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="row">
            <div className="col-12">
              <NotificationManager />
            </div>
          </div>
        )}

        {/* Provider Currencies Tab */}
        {activeTab === "provider-currencies" && (
          <div className="row">
            <div className="col-12">
              <ProviderCurrencyManagement />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
